import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ReactFormGenerator } from 'react-form-builder2';
import Modal from '../Modal';
import MasterDataSelector from './MasterDataSelector';
import ProductManagement from './ProductManagement';
import api from '../../services/api';
//import { toast } from 'react-toastify';
import toast from '../../utils/toast';
import { createVerificationRecord } from '../../utils/documentVerification';

const FormSubmissionEdit = ({ isOpen, onClose, submission, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fullSubmission, setFullSubmission] = useState(null);
  const [form, setForm] = useState(null);
  
  // Form data state
  const [formData, setFormData] = useState({});
  const [productsData, setProductsData] = useState({});
  const [selectedMasterData, setSelectedMasterData] = useState(null);
  
  // Master data selection states
  const [masterDataList, setMasterDataList] = useState([]);
  const [masterDataLoading, setMasterDataLoading] = useState(false);
  const [masterDataSearchTerm, setMasterDataSearchTerm] = useState('');
  const [clientTypes, setClientTypes] = useState([]);
  const [selectedClientType, setSelectedClientType] = useState('');
  const [showMasterDataDropdown, setShowMasterDataDropdown] = useState(false);

  // Product management states
  const [showProductModal, setShowProductModal] = useState(false);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState({});

  // Handle clicking outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMasterDataDropdown && !event.target.closest('.master-data-dropdown')) {
        setShowMasterDataDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMasterDataDropdown]);

  useEffect(() => {
    if (isOpen && submission) {
      fetchCompleteSubmission();
      fetchMasterData();
      fetchClientTypes();
      fetchAvailableProducts();
    }
  }, [isOpen, submission]);

  // Load master data when submission is available
  useEffect(() => {
    if (fullSubmission && fullSubmission.masterDataId && masterDataList.length > 0) {
      console.log('Loading master data:', fullSubmission.masterDataId, masterDataList);
      const masterDataId = fullSubmission.masterDataId._id || fullSubmission.masterDataId;
      const masterData = masterDataList.find(item => item._id === masterDataId);
      if (masterData) {
        console.log('Found master data:', masterData);
        setSelectedMasterData(masterData);
      } else {
        console.log('Master data not found for ID:', masterDataId);
      }
    }
  }, [fullSubmission, masterDataList]);

  const fetchCompleteSubmission = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/form-submissions/${submission._id}`);
      const completeSubmission = response.data;
      console.log('Complete submission data:', completeSubmission);
      console.log('Master data ID:', completeSubmission.masterDataId);
      setFullSubmission(completeSubmission);
      
      // Initialize form data
      initializeFormData(completeSubmission);
      
      // Fetch the form
      if (completeSubmission.formId) {
        await fetchForm(completeSubmission.formId);
      }
    } catch (error) {
      console.error('Failed to fetch complete submission:', error);
      toast.error('Failed to load submission data');
    } finally {
      setLoading(false);
    }
  };

  const initializeFormData = (completeSubmission) => {
    console.log('=== FRONTEND EDIT: Initializing form data');
    console.log('productsData length:', completeSubmission.productsData?.length || 0);
    console.log('submissionData.products length:', completeSubmission.submissionData?.products?.length || 0);
    
    // Initialize form fields
    let formFields = {};
    if (completeSubmission.submissionData?.formFields) {
      completeSubmission.submissionData.formFields.forEach(field => {
        formFields[field.name] = field.value;
      });
    }
    setFormData(formFields);
    
    // Initialize products data using productsData as the primary source of truth
    const normalizedProductsData = {};
    
    // Use productsData as the primary source (this is the authoritative data)
    if (completeSubmission.productsData && completeSubmission.productsData.length > 0) {
      console.log('=== FRONTEND EDIT: Using productsData as primary source');
      completeSubmission.productsData.forEach((product, productIndex) => {
        const key = `${product.productIndex || productIndex}`;
        normalizedProductsData[key] = {
          id: product.id._id || product.id,
          name: product.id.name || product.name,
          instance: 0,
          productIndex: product.productIndex || productIndex,
          // Initialize fees from product data or use defaults
          'Government Fees': product.fees?.governmentFees?.amount || 0,
          'Professional Fees': product.fees?.professionalFees?.amount || 0,
          'Other Fees': product.fees?.otherFees?.reduce((acc, fee) => {
            acc[fee.name] = fee.amount || 0;
            return acc;
          }, {}) || {},
          // Initialize required fields from product data
          ...(product.requiredFields || []).reduce((acc, field) => {
            acc[field.label] = field.value || '';
            return acc;
          }, {}),
          // Use existing documents and verifications
          documents: product.documents || [],
          verifications: product.verifications || []
        };
        
        // Also preserve any field values that might be stored as documents
        if (product.documents) {
          product.documents.forEach(doc => {
            if (doc.requirementName && doc.document?.url) {
              normalizedProductsData[key][doc.requirementName] = doc.document.url;
            }
          });
        }
      });
    } else if (completeSubmission.submissionData?.products) {
      // Fallback to submissionData.products if productsData is empty
      console.log('=== FRONTEND EDIT: Falling back to submissionData.products');
      completeSubmission.submissionData.products.forEach((product, productIndex) => {
        const key = `${productIndex}`;
        normalizedProductsData[key] = {
          id: product.id,
          name: product.name,
          instance: 0,
          productIndex: productIndex,
          'Government Fees': product.fees?.governmentFees?.amount || 0,
          'Professional Fees': product.fees?.professionalFees?.amount || 0,
          'Other Fees': product.fees?.otherFees?.reduce((acc, fee) => {
            acc[fee.name] = fee.amount || 0;
            return acc;
          }, {}) || {},
          // Initialize required fields
          ...(product.requiredFields || []).reduce((acc, field) => {
            acc[field.label] = field.value || '';
            return acc;
          }, {}),
          // Initialize empty arrays for documents and verifications
          documents: [],
          verifications: []
        };
      });
    }
    
    console.log('=== FRONTEND EDIT: Final normalized products data:', normalizedProductsData);
    setProductsData(normalizedProductsData);
  };

  const fetchForm = async (formId) => {
    try {
      const formIdString = typeof formId === 'string' ? formId : formId._id;
      const response = await api.get(`/forms/${formIdString}`);
      setForm(response.data.data);
    } catch (error) {
      console.error('Failed to fetch form:', error);
    }
  };

  const fetchMasterData = async () => {
    try {
      setMasterDataLoading(true);
      const response = await api.get('/master-data');
      console.log('Master data response:', response.data);
      setMasterDataList(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch master data:', error);
      toast.error('Failed to load master data');
    } finally {
      setMasterDataLoading(false);
    }
  };

  const fetchClientTypes = async () => {
    try {
      const response = await api.get('/admin/client-types');
      setClientTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch client types:', error);
    }
  };

  const fetchAvailableProducts = async () => {
    try {
      const response = await api.get('/admin/products');
      setAvailableProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      toast.error('Failed to load products');
    }
  };

  const handleMasterDataSelect = (masterData) => {
    setSelectedMasterData(masterData);
    setShowMasterDataDropdown(false);
    setMasterDataSearchTerm('');
  };

  const handleAddProduct = () => {
    setShowProductModal(true);
  };

  const handleProductSelect = (product) => {
    // Find the next available productIndex that doesn't conflict with existing ones
    const existingIndexes = Object.values(productsData).map(p => p.productIndex || 0);
    const nextIndex = Math.max(...existingIndexes, -1) + 1;
    const key = `${nextIndex}`;
    
    const newProductData = {
      id: product._id,
      name: product.name,
      instance: 0,
      productIndex: nextIndex, // Assign the next available index
      'Government Fees': product.fees.governmentFees.amount,
      'Professional Fees': product.fees.professionalFees.amount,
      'Other Fees': product.fees.otherFees.reduce((acc, fee) => {
        acc[fee.name] = fee.amount;
        return acc;
      }, {}),
      // Initialize required fields
      ...(product.requiredFields || []).reduce((acc, field) => {
        acc[field.label] = '';
        return acc;
      }, {}),
      // Initialize empty arrays for documents and verifications
      documents: [],
      verifications: []
    };
    
    setProductsData(prev => ({
      ...prev,
      [key]: newProductData
    }));
    setShowProductModal(false);
  };

  const handleRemoveProduct = (productIndex) => {
    const newProductsData = {};
    Object.entries(productsData).forEach(([key, data]) => {
      const [index] = key.split('_');
      if (parseInt(index) !== parseInt(productIndex)) {
        // Keep the original key to maintain productIndex consistency
        newProductsData[key] = data;
      }
    });
    setProductsData(newProductsData);
  };

  const handleFileUpload = async (productIndex, fieldName, file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      setUploadingFiles(prev => ({ ...prev, [`${productIndex}`]: true }));
      const response = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const fileUrl = response.data.url;
      
      // Store the file URL as a field value (for backward compatibility)
      handleFieldChange(productIndex, fieldName, fileUrl);
      
      // Also store the document in the documents array format that the backend expects
      const document = {
        productId: productsData[productIndex]?.id,
        productIndex: productsData[productIndex]?.productIndex || parseInt(productIndex),
        requirementName: fieldName,
        document: {
          name: file.name,
          url: fileUrl,
          originalName: file.name,
          mimeType: file.type,
          size: file.size,
          uploadedAt: Date.now()
        }
      };
      
      setProductsData(prev => ({
        ...prev,
        [productIndex]: {
          ...prev[productIndex],
          documents: [...(prev[productIndex]?.documents || []), document]
        }
      }));

      // Create verification record for the uploaded document
      try {
        const verificationData = {
          documentType: 'formSubmission',
          documentId: submission._id, // Form submission ID
          documentField: fieldName,
          fileUrl: fileUrl,
          fileName: file.name,
          fileSize: file.size
        };

        const verificationResult = await createVerificationRecord(verificationData);
        
        if (verificationResult.verificationRequired) {
          toast.info('Document uploaded successfully. Verification required before approval.');
        } else {
          toast.success('File uploaded successfully');
        }
      } catch (verificationError) {
        console.error('Verification error:', verificationError);
        toast.success('File uploaded successfully');
      }
    } catch (error) {
      console.error('File upload failed:', error);
      toast.error('File upload failed');
    } finally {
      setUploadingFiles(prev => ({ ...prev, [`${productIndex}`]: false }));
    }
  };

  const handleFieldChange = (productKey, fieldName, value) => {
    console.log(productKey, fieldName, value);
    setProductsData(prev => ({
      ...prev,
      [productKey]: {
        ...prev[productKey],
        [fieldName]: value
      }
    }));
    console.log(productsData);
  };

  const handleFeeChange = (productKey, feeType, feeName, value) => {
    setProductsData(prev => ({
      ...prev,
      [productKey]: {
        ...prev[productKey],
        [feeType]: feeType === 'Other Fees' 
          ? { ...prev[productKey][feeType], [feeName]: parseFloat(value) || 0 }
          : parseFloat(value) || 0
      }
    }));
  };

  // Handle form changes from ReactFormGenerator
  const handleFormChange = (data) => {
    // data is an array of {name, value}
    const newFormData = {};
    data.forEach(item => {
      newFormData[item.name] = item.value;
    });
    setFormData(newFormData);
  };

  const calculateTotalFees = (productKey) => {
    const product = productsData[productKey];
    if (!product) return 0;
    
    const governmentFees = parseFloat(product['Government Fees']) || 0;
    const professionalFees = parseFloat(product['Professional Fees']) || 0;
    const otherFees = Object.values(product['Other Fees'] || {}).reduce((sum, fee) => sum + (parseFloat(fee) || 0), 0);
    
    return governmentFees + professionalFees + otherFees;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedMasterData) {
      toast.error('Please select a client');
      return;
    }

    try {
      setSubmitting(true);
      
      // Prepare submission data
      console.log("productsData", productsData);
      const submissionData = {
        formFields: Object.entries(formData).map(([name, value]) => ({ name, value })),
        products: Object.entries(productsData).map(([key, productData]) => {
          const product = availableProducts.find(p => p._id === productData.id);
          console.log("Searched product", product, productData.id);
          return {
            id: productData.id,
            name: product?.name || 'Unknown Product',
            fees: {
              governmentFees: { amount: parseFloat(productData['Government Fees']) || 0 },
              professionalFees: { amount: parseFloat(productData['Professional Fees']) || 0 },
              otherFees: Object.entries(productData['Other Fees'] || {}).map(([name, amount]) => ({
                name,
                amount: parseFloat(amount) || 0
              }))
            },
            requiredFields: product?.requiredFields?.map(field => ({
              label: field.label,
              value: productData[field.label] || '',
              fieldType: field.fieldType,
              isRequired: field.isRequired
            })) || []
          };
        })
      };

      // Prepare productsData with documents for backend update
      const productsDataForUpdate = Object.entries(productsData).map(([key, productData]) => {
        const product = availableProducts.find(p => p._id === productData.id);
        return {
          id: productData.id,
          name: product?.name || 'Unknown Product',
          productIndex: productData.productIndex || parseInt(key), // Use original productIndex if available
          documents: productData.documents || [],
          verifications: productData.verifications || [],
          requiredFields: product?.requiredFields?.map(field => ({
            label: field.label,
            value: productData[field.label] || '',
            fieldType: field.fieldType,
            isRequired: field.isRequired
          })) || []
        };
      });

      console.log("Submission data", submissionData);
      console.log("Products data for update", productsDataForUpdate);

      const updateData = {
        masterDataId: selectedMasterData._id,
        submissionData,
        productsData: productsDataForUpdate
      };

      const response = await api.put(`/form-submissions/${submission._id}`, updateData);
      
      toast.success('Form submission updated successfully');
      onUpdate(response.data);
      onClose();
    } catch (error) {
      console.error('Failed to update submission:', error);
      toast.error('Failed to update submission');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  if (loading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" title="Edit Form Submission">
      <div className="bg-white rounded-lg">
        <div className="p-6 space-y-6 max-h-[80vh] overflow-y-auto">
          {/* Master Data Selector */}
          <MasterDataSelector
            selectedMasterData={selectedMasterData}
            setSelectedMasterData={setSelectedMasterData}
            masterDataList={masterDataList}
            masterDataLoading={masterDataLoading}
            masterDataSearchTerm={masterDataSearchTerm}
            setMasterDataSearchTerm={setMasterDataSearchTerm}
            clientTypes={clientTypes}
            selectedClientType={selectedClientType}
            setSelectedClientType={setSelectedClientType}
            showMasterDataDropdown={showMasterDataDropdown}
            setShowMasterDataDropdown={setShowMasterDataDropdown}
            handleMasterDataSelect={handleMasterDataSelect}
          />

          {/* Form Fields using ReactFormGenerator */}
          {form && form.schema && form.schema.task_data && form.schema.task_data.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Form Information</h3>
              <ReactFormGenerator
                data={form.schema.task_data}
                answer_data={Object.entries(formData).map(([name, value]) => ({ name, value }))}
                onChange={handleFormChange}
                hide_actions={true}
              />
            </div>
          )}

          {/* Product Management */}
          <ProductManagement
            productsData={productsData}
            availableProducts={availableProducts}
            showProductModal={showProductModal}
            setShowProductModal={setShowProductModal}
            handleAddProduct={handleAddProduct}
            handleRemoveProduct={handleRemoveProduct}
            handleProductSelect={handleProductSelect}
            handleFieldChange={handleFieldChange}
            handleFeeChange={handleFeeChange}
            calculateTotalFees={calculateTotalFees}
            uploadingFiles={uploadingFiles}
            handleFileUpload={handleFileUpload}
          />

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {submitting ? 'Updating...' : 'Update Submission'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default FormSubmissionEdit; 